import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  User,
  Lock,
  Phone,
  Building2,
  Briefcase,
  DollarSign,
  CheckCircle,
  Eye,
  EyeOff,
  KeyRound,
  UserCheck2,
  Upload,
  Loader2,
} from "lucide-react";

import { ValidateInput } from "@/components/SearchBox/ValidateInput";
import { validateEmail, validateUsername } from "@/utils/validators";
import { saveUserInfo, UpdateUserInfo } from "@/redux/Masters/users/actions";
import { showToast } from "@/redux/Toast/actions";
import useAuthUser from "@/hooks/useAuthUser";

/* ─── Style tokens ───────────────────────────────────────────── */
const inputBase =
  "w-full px-3 py-2 bg-white dark:bg-slate-800 border rounded-lg text-sm " +
  "text-slate-900 dark:text-white placeholder-slate-400 " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
const labelBase =
  "block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const selectBase =
  "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 " +
  "rounded-lg text-sm text-slate-900 dark:text-white appearance-none cursor-pointer " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
const errorBase = "mt-1 text-[11px] text-red-500 font-medium";

/* ─── Required fields ────────────────────────────────────────── */
const REQUIRED_FIELDS = [
  { key: "FIRSTNAME", label: "First Name" },
  { key: "EMAILID", label: "Email Address" },
  { key: "USERNAME", label: "Username" },
  { key: "ROLEID", label: "Role" },
  { key: "OFFICEID", label: "Office Type" },
];
const PASSWORD_FIELD = { key: "PASSWORD", label: "Password" };

/* ─── Field wrapper ──────────────────────────────────────────── */
function Field({ label, required, error, children }) {
  return (
    <div>
      <label className={labelBase}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className={errorBase}>{error}</p>}
    </div>
  );
}

/* ─── Section divider ────────────────────────────────────────── */
function SectionDivider({ icon: Icon, title, color = "blue" }) {
  const colors = {
    blue: "bg-blue-500",
    green: "bg-emerald-500",
    purple: "bg-purple-500",
    amber: "bg-amber-500",
  };
  return (
    <div className="flex items-center gap-2 py-1">
      <div className={`p-1.5 rounded-lg ${colors[color]} shadow-sm shrink-0`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 whitespace-nowrap">
        {title}
      </span>
      <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

/* ─── Image Upload Field ─────────────────────────────────────── */
function ImageUploadField({
  value,
  onChange,
  label = "Profile Image",
  accept = "image/png,image/jpeg,image/webp",
  hint = "PNG, JPG, WEBP · Max 5 MB",
}) {
  const fileRef = useRef(null);

  /* ── Read file → base-64 data-URL ── */
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    console.log("Selected file:", file);
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => onChange(reader.result, file);
    reader.readAsDataURL(file);
  };

  /* ── Clear selection ── */
  const handleRemove = () => {
    onChange(null, null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div>
      <label className={labelBase}>{label}</label>

      {value ? (
        /* ── Filled state ──────────────────────────────────────────── */
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg
                                border border-slate-200 dark:border-slate-700
                                bg-slate-50 dark:bg-slate-800"
        >
          <img
            src={value}
            alt="Profile preview"
            className="w-11 h-11 rounded-lg object-cover shrink-0
                                   border border-slate-200 dark:border-slate-600"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />

          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
              Image selected
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Click "Change" to replace or "Remove" to clear
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Change */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="h-8 px-3 rounded-lg text-[12px] font-semibold
                                       bg-white dark:bg-slate-700
                                       border border-slate-300 dark:border-slate-600
                                       text-slate-700 dark:text-slate-200
                                       hover:bg-slate-100 transition-colors"
            >
              Change
            </button>

            {/* Remove */}
            <button
              type="button"
              onClick={handleRemove}
              className="h-8 px-3 rounded-lg text-[12px] font-semibold
                                       inline-flex items-center gap-1
                                       bg-red-50 dark:bg-red-900/20
                                       border border-red-200 dark:border-red-800
                                       text-red-600 dark:text-red-400
                                       hover:bg-red-100 transition-colors"
            >
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ) : (
        /* ── Empty / upload state ──────────────────────────────────── */
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                               border border-dashed border-slate-300 dark:border-slate-600
                               bg-slate-50 dark:bg-slate-800/50
                               hover:border-blue-400 hover:bg-blue-50/30
                               transition-all group"
        >
          <div
            className="w-9 h-9 rounded-lg shrink-0
                                    bg-slate-200 dark:bg-slate-700
                                    group-hover:bg-blue-100
                                    flex items-center justify-center transition-colors"
          >
            <Upload className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
          </div>
          <div className="text-left">
            <p
              className="text-[12px] font-semibold
                                      text-slate-600 dark:text-slate-300
                                      group-hover:text-blue-600 transition-colors"
            >
              Upload profile image
            </p>
            <p className="text-[11px] text-slate-400">{hint}</p>
          </div>
        </button>
      )}

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Main Modal
══════════════════════════════════════════════════════════════ */
const UserModal = ({
  open,
  onClose,
  formData,
  errors,
  handleInputChange,
  onSave,
  roleList = [],
}) => {
  const dispatch = useDispatch();
  const { officeTypeList } = useSelector((state) => state.common);

  const [showPassword, setShowPassword] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const authUser = useAuthUser();
  const isEdit = Number(formData?.id) > 0;

  const [MasterCompanyDetails] = JSON.parse(
    localStorage.getItem("MasterCompanyDetails") ?? "[{}]",
  );

  // Reset local state whenever modal opens
  useEffect(() => {
    if (open) {
      setShowPassword(false);
      setProfileImage(formData?.PROFILEIMAGE || null);
      setProfileImageFile(null);
    }
  }, [open]);

  /* ─── Validate required fields ──────────────────────────── */
  const validateFields = () => {
    const fields = [...REQUIRED_FIELDS];
    if (!isEdit) fields.push(PASSWORD_FIELD);

    const missing = fields.filter((f) => {
      const val = formData?.[f.key];
      return (
        !val || String(val).trim() === "" || val === "0" || Number(val) === 0
      );
    });

    if (missing.length) {
      dispatch(
        showToast(
          `Required fields missing: ${missing.map((f) => f.label).join(", ")}`,
          "error",
        ),
      );
      return false;
    }
    return true;
  };

  /* ─── Save handler ──────────────────────────────────────── */
  const handleSave = async () => {
    if (!validateFields()) return;

    setIsSaving(true);
    try {
      const createdBy =
        `${authUser?.firstName || ""} ${authUser?.lastName || ""}`.trim() ||
        authUser?.username ||
        "";

      const fd = new FormData();
      if (isEdit) fd.append("USERID", Number(formData?.id || 0));
      fd.append("FIRSTNAME", formData?.FIRSTNAME || "");
      fd.append("LASTNAME", formData?.LASTNAME || "");
      fd.append("EMAILID", formData?.EMAILID || "");
      fd.append("CONTACTNO", formData?.CONTACTNO || "");
      fd.append("USERNAME", formData?.USERNAME || "");
      fd.append("PASSWORD", formData?.PASSWORD || "");
      fd.append("ROLEID", Number(formData?.ROLEID) || 0);
      fd.append("OFFICEID", Number(formData?.OFFICEID) || 0);
      fd.append("OFFICETYPE", formData?.OFFICETYPE || "");
      fd.append("PROFITCENTER", formData?.Profitcenter || "");
      fd.append("PROFITTYPE", formData?.profitType || "");
      fd.append("PROFIT", parseFloat(formData?.profit) || 0);
      fd.append("IS_SALESPERSON", formData?.salesperson ?? false);
      fd.append("IS_FILEMANAGER", formData?.IS_FILEMANAGER ?? false);
      fd.append("ISACTIVE", formData?.isActive ?? true);
      fd.append(
        "COMPANYID",
        Number(formData?.companY_ID || MasterCompanyDetails?.CompanyId || 0),
      );
      fd.append("COMPANY_NAME", MasterCompanyDetails?.CompanyName || "");
      fd.append("CREATED_BY", createdBy);
      fd.append("MODIFIED_BY", createdBy);

      // Profile image — use original File object to preserve filename
      if (profileImageFile instanceof File) {
        fd.append("PROFILE_IMAGE", profileImageFile, profileImageFile.name);
      } else if (
        profileImage &&
        typeof profileImage === "string" &&
        !profileImage.startsWith("data:")
      ) {
        fd.append("PROFILE_IMAGE", profileImage);
      } else {
        fd.append("PROFILE_IMAGE", "");
      }

      if (isEdit) {
        await dispatch(UpdateUserInfo(fd));
      } else {
        await dispatch(saveUserInfo(fd));
      }

      dispatch(
        showToast(
          isEdit ? "User updated successfully!" : "User created successfully!",
          "success",
          true,
        ),
      );
      onSave?.();
      onClose();
    } catch (err) {
      dispatch(showToast("Something went wrong. Please try again.", "error"));
      console.error("Save/Update user error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  console.log("offiece type list in modal:", officeTypeList);

  // ValidateInput class — matches the rest of the form inputs
  const validateInputClass =
    "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 " +
    "text-slate-900 dark:text-white focus:ring-blue-500";

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-600 to-slate-700 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">
                    {isEdit ? "Edit User" : "Add New User"}
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    {isEdit
                      ? `Editing: ${formData?.FIRSTNAME || ""} ${formData?.LASTNAME || ""}`
                      : "Fill in all required fields to create a user"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* IDENTITY */}
              <SectionDivider icon={User} title="Identity" color="blue" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First Name" required error={errors?.FIRSTNAME}>
                  <input
                    name="FIRSTNAME"
                    value={formData?.FIRSTNAME || ""}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    autoComplete="off"
                    className={`${inputBase} ${errors?.FIRSTNAME ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                  />
                </Field>

                <Field label="Last Name" error={errors?.LASTNAME}>
                  <input
                    name="LASTNAME"
                    value={formData?.LASTNAME || ""}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    autoComplete="off"
                    className={`${inputBase} ${errors?.LASTNAME ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                  />
                </Field>

                <div>
                  <label className={labelBase}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <ValidateInput
                    type="email"
                    value={formData?.EMAILID || ""}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "EMAILID", value },
                      })
                    }
                    placeholder="user@company.com"
                    minLength={5}
                    debounceTime={700}
                    inputClassName={validateInputClass}
                    validator={validateEmail}
                    autoComplete="off"
                  />
                  {errors?.EMAILID && (
                    <p className={errorBase}>{errors.EMAILID}</p>
                  )}
                </div>

                <Field label="Contact No." error={errors?.CONTACTNO}>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      name="CONTACTNO"
                      value={formData?.CONTACTNO || ""}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                      autoComplete="off"
                      className={`${inputBase} pl-9 border-slate-200 dark:border-slate-700`}
                    />
                  </div>
                </Field>

                <div className="sm:col-span-2">
                  <ImageUploadField
                    value={profileImage || formData?.PROFILE_IMAGE || null}
                    onChange={(dataUrl, file) => {
                      setProfileImage(dataUrl);
                      setProfileImageFile(file instanceof File ? file : null);
                      handleInputChange({
                        target: { name: "PROFILE_IMAGE", value: file },
                      });
                      console.log("Profile image changed:", {
                        dataUrl,
                        file,
                      });
                    }}
                  />
                </div>
              </div>

              {/* CREDENTIALS */}
              <SectionDivider
                icon={KeyRound}
                title="Credentials"
                color="purple"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Username — ValidateInput for live duplicate check UI only.
                                    No status gating on save button. */}
                <div>
                  <label className={labelBase}>
                    Username <span className="text-red-500">*</span>
                  </label>
                  <ValidateInput
                    value={formData?.USERNAME || ""}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "USERNAME", value },
                      })
                    }
                    placeholder="Enter username"
                    minLength={3}
                    debounceTime={700}
                    inputClassName={validateInputClass}
                    validator={validateUsername}
                    autoComplete="off"
                  />
                  {errors?.USERNAME && (
                    <p className={errorBase}>{errors.USERNAME}</p>
                  )}
                </div>

                <Field
                  label="Password"
                  required={!isEdit}
                  error={errors?.PASSWORD}
                >
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      name="PASSWORD"
                      type={showPassword ? "text" : "password"}
                      value={formData?.PASSWORD || ""}
                      onChange={handleInputChange}
                      placeholder="Min 8 characters"
                      autoComplete="new-password"
                      className={`${inputBase} pl-9 pr-9 ${errors?.PASSWORD ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-3.5 h-3.5" />
                      ) : (
                        <Eye className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </Field>
              </div>

              {/* ORGANISATION */}
              <SectionDivider
                icon={Building2}
                title="Organisation"
                color="green"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Role — hardcoded options */}
                <Field label="Role" required error={errors?.ROLEID}>
                  <select
                    name="ROLEID"
                    value={formData?.ROLEID || ""}
                    onChange={handleInputChange}
                    className={`${selectBase} ${errors?.ROLEID ? "border-red-400 focus:ring-red-400" : ""}`}
                  >
                    <option value="">— Select Role —</option>
                    {roleList?.length > 0 ? (
                      roleList.map((r) => (
                        <option key={r.roleId} value={r.roleId}>
                          {r.roleName}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="2">Company Admin</option>
                        <option value="3">Operation User</option>
                        <option value="4">Manager</option>
                        <option value="5">DATA ENTRY</option>
                        <option value="6">Viewer</option>
                        <option value="7">Accounts</option>
                      </>
                    )}
                  </select>
                </Field>

                <Field label="Office Type" required error={errors?.OFFICEID}>
                  <select
                    name="OFFICEID"
                    value={formData?.OFFICEID || ""}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    className={`${selectBase} ${errors?.OFFICEID ? "border-red-400 focus:ring-red-400" : ""}`}
                  >
                    <option value="">— Select Office —</option>
                    {officeTypeList?.map((item, index) => (
                      <option key={index} value={item.id}>
                        {item.officeType}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Profit Center" error={errors?.Profitcenter}>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      name="Profitcenter"
                      value={formData?.Profitcenter || ""}
                      onChange={handleInputChange}
                      placeholder="e.g. CORP-01"
                      autoComplete="off"
                      className={`${inputBase} pl-9 ${errors?.Profitcenter ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                    />
                  </div>
                </Field>

                <Field label="Profit Type" error={errors?.profitType}>
                  <select
                    name="profitType"
                    value={formData?.profitType || ""}
                    onChange={handleInputChange}
                    className={selectBase}
                  >
                    <option value="">— Select Profit Type —</option>
                    <option value="FIXED">Fixed</option>
                    <option value="PERCENTAGE">Percentage</option>
                  </select>
                </Field>

                <Field label="Profit Amount" error={errors?.profit}>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      name="profit"
                      type="number"
                      step="0.01"
                      value={formData?.profit || ""}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      autoComplete="off"
                      className={`${inputBase} pl-9 border-slate-200 dark:border-slate-700`}
                    />
                  </div>
                </Field>
              </div>

              {/* PERMISSIONS */}
              <SectionDivider
                icon={CheckCircle}
                title="Permissions & Status"
                color="amber"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { name: "salesperson", label: "Is Sales Person" },

                  { name: "IS_FILEMANAGER", label: "File Manager" },
                  { name: "isActive", label: "Is Active" },
                ].map((item) => (
                  <label
                    key={item.name}
                    className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition-colors group"
                  >
                    <div className="relative shrink-0">
                      <input
                        type="checkbox"
                        name={item.name}
                        checked={formData?.[item.name] ?? false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 rounded-full bg-slate-200 dark:bg-slate-600 peer-checked:bg-blue-500 transition-colors" />
                      <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                    </div>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>

              <p className="text-[11px] text-slate-400 dark:text-slate-500">
                <span className="text-red-500">*</span> Denotes required fields
              </p>
            </div>

            {/* ── Footer ── */}
            <div className="shrink-0 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium text-red-500 hover:text-red-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className={`inline-flex items-center gap-2 h-9 px-5 rounded-lg text-sm font-semibold text-white transition-all
                                    ${
                                      isSaving
                                        ? "bg-slate-400 cursor-not-allowed opacity-60"
                                        : "bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]"
                                    }`}
              >
                {isSaving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <UserCheck2 className="w-3.5 h-3.5" />
                )}
                {isEdit ? "Update User" : "Create User"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UserModal;
